// templateEngine.js
// ─────────────────────────────────────────────────────────────────────────────
// Token merge for client document templates (HTML format).
//
// Templates are HTML strings stored per client in Firestore. They carry tokens
// that are replaced with captured register data at populate time:
//
//   {{field}}             → data[field]; EMPTY or MISSING → "n/a"
//   {{check:field=value}} → "☒" if data[field] === value (case-insensitive),
//                           else "☐"  (drives checkbox groups: As-Built Yes/No,
//                           Financial Implications options, etc.)
//   {{date:field}}        → data[field] formatted as "09 June 2026", else "n/a"
//
// Text values are HTML-escaped; the template itself is trusted authored HTML.
// The filled HTML is rendered to PDF with the app's standard window.print()
// path (same as VOProformaPDF / printInstruction).
// ─────────────────────────────────────────────────────────────────────────────

export const NA = "n/a";

export function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) =>
    ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

const BLOCKED_TEMPLATE_TAGS = [
  "script",
  "iframe",
  "object",
  "embed",
  "base",
  "link",
  "meta",
  "form",
  "input",
  "button",
  "textarea",
  "select",
  "option",
  "svg",
  "math",
];

function isSafeTemplateUrl(value) {
  const v = [...String(value || "").trim()]
    .filter((ch) => {
      const code = ch.charCodeAt(0);
      return code > 31 && code !== 127 && !/\s/.test(ch);
    })
    .join("");
  if (!v) return true;
  if (/^(#|\/|\.\/|\.\.\/)/.test(v)) return true;
  if (/^(https?:|mailto:|tel:)/i.test(v)) return true;
  if (/^data:image\/(?:png|jpe?g|gif|webp);base64,/i.test(v)) return true;
  return false;
}

function sanitizeStyle(value) {
  return String(value || "")
    .replace(/expression\s*\([^)]*\)/gi, "")
    .replace(/url\s*\(\s*(['"]?)\s*(?:javascript:|data:(?!image\/(?:png|jpe?g|gif|webp);base64,))[^)]*\1\s*\)/gi, "");
}

function regexSanitizeTemplateHtml(html) {
  let out = String(html || "");
  out = out.replace(/<\s*(script|iframe|object|embed|base|link|meta|form|input|button|textarea|select|option|svg|math)\b[\s\S]*?<\s*\/\s*\1\s*>/gi, "");
  out = out.replace(/<\s*(script|iframe|object|embed|base|link|meta|form|input|button|textarea|select|option|svg|math)\b[^>]*\/?\s*>/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*(['"])[\s\S]*?\1/gi, "");
  out = out.replace(/\s+on[a-z]+\s*=\s*[^\s>]+/gi, "");
  out = out.replace(/\s+(?:href|src|action|formaction|poster|srcdoc|xlink:href)\s*=\s*(['"])\s*(?:javascript:|data:(?!image\/(?:png|jpe?g|gif|webp);base64,))[\s\S]*?\1/gi, "");
  return out.replace(/\s+style\s*=\s*(['"])([\s\S]*?)\1/gi, (_, quote, style) => ` style=${quote}${sanitizeStyle(style)}${quote}`);
}

export function sanitizeTemplateHtml(html) {
  const source = String(html || "");
  if (!source) return "";
  if (typeof DOMParser === "undefined") return regexSanitizeTemplateHtml(source);

  const parser = new DOMParser();
  const doc = parser.parseFromString(source, "text/html");
  doc.querySelectorAll(BLOCKED_TEMPLATE_TAGS.join(",")).forEach((node) => node.remove());
  doc.querySelectorAll("style").forEach((node) => {
    node.textContent = sanitizeStyle(node.textContent || "");
  });

  doc.querySelectorAll("*").forEach((node) => {
    for (const attr of [...node.attributes]) {
      const name = attr.name.toLowerCase();
      const value = attr.value || "";
      if (name.startsWith("on") || name === "srcdoc") {
        node.removeAttribute(attr.name);
      } else if (["href", "src", "action", "formaction", "poster", "xlink:href"].includes(name)) {
        if (!isSafeTemplateUrl(value)) node.removeAttribute(attr.name);
      } else if (name === "style") {
        node.setAttribute(attr.name, sanitizeStyle(value));
      }
    }
  });

  const headStyles = [...doc.head.querySelectorAll("style")].map((node) => node.outerHTML).join("");
  return headStyles + doc.body.innerHTML;
}

function rawVal(data, field) {
  const v = data ? data[field] : undefined;
  if (v === null || v === undefined || String(v).trim() === "") return null;
  return v;
}

function fmtDate(v) {
  if (v === null) return null;
  const str = String(v);
  const d = new Date(str + (str.length === 10 ? "T00:00:00" : ""));
  if (isNaN(d)) return str;
  return d.toLocaleDateString("en-ZA", { day: "2-digit", month: "long", year: "numeric" });
}

// Replace every token in `html` from the flat `data` map. Missing → "n/a".
export function renderTemplate(html, data = {}) {
  if (!html) return "";
  return sanitizeTemplateHtml(html)
    // {{check:field=value}} — checkbox cells
    .replace(/\{\{\s*check:\s*([\w.]+)\s*=\s*([^}]+?)\s*\}\}/gi, (_, field, want) => {
      const v = rawVal(data, field);
      if (v === null) return "☐";
      return String(v).trim().toLowerCase() === String(want).trim().toLowerCase() ? "☒" : "☐";
    })
    // {{date:field}}
    .replace(/\{\{\s*date:\s*([\w.]+)\s*\}\}/gi, (_, field) => {
      const v = fmtDate(rawVal(data, field));
      return v === null ? NA : escapeHtml(v);
    })
    // {{field}}
    .replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, field) => {
      const v = rawVal(data, field);
      return v === null ? NA : escapeHtml(v).replace(/\n/g, "<br/>");
    });
}

// Tokens referenced by a template — powers the "fields used" hint and validation.
export function extractTokens(html) {
  const out = new Set();
  const re = /\{\{\s*(?:check:|date:)?\s*([\w.]+)/g;
  let m;
  while ((m = re.exec(html || ""))) out.add(m[1]);
  return [...out];
}

// ─── Field vocabularies per document type ────────────────────────────────────
// What a captured register row + the project expose to a template.

export function instructionFields(row = {}, project = {}) {
  return {
    siNumber: row.siNumber || row.id || "",
    projectNumber: row.projectNumber || project.id || project.projectNumber || "",
    clientProjectNumber: project.clientProjectNumber || project.consultantProjectNumber || "",
    contractNumber: row.contractNumber || project.contractNumber || "",
    contractDescription: row.contractDescription || project.name || project.contractDescription || "",
    contractor: row.contractor || project.contractor || "",
    subject: row.subject || "",
    instruction: row.instructionText || "",
    instructionText: row.instructionText || "",
    applicablePayItems: row.applicablePayItems || "",
    drawingRevisionRef: row.drawingRevisionRef || "",
    asBuiltImplications: row.asBuiltImplications || "",
    financialImplications: row.financialImplications || "",
    valuationBasis: row.valuationBasis || "",
    employersAgentRepresentative: row.employersAgentRepresentative || row.issuedBy || "",
    issuedBy: row.issuedBy || "",
    instructionDate: row.instructionDate || "",
    instructionTime: row.instructionTime || "",
    contractorRemarks: row.contractorRemarks || "",
    receivedBy: row.receivedBy || "",
    receivedDate: row.receivedDate || "",
    receivedTime: row.receivedTime || "",
    distribution: row.distribution || "",
  };
}

export const FIELD_VOCAB = {
  instruction: [
    "siNumber", "projectNumber", "clientProjectNumber", "contractNumber",
    "contractDescription", "contractor", "subject", "instruction",
    "applicablePayItems", "drawingRevisionRef", "asBuiltImplications",
    "financialImplications", "valuationBasis", "employersAgentRepresentative",
    "instructionDate", "instructionTime", "contractorRemarks", "receivedBy",
    "receivedDate", "receivedTime", "distribution",
  ],
};

export function fieldsFor(docType, row, project) {
  switch (docType) {
    case "instruction": return instructionFields(row, project);
    default: return { ...row };
  }
}

// Isolation key for templates: a client's templates are shared across that
// client's projects but invisible to every other client. Projects not yet
// assigned to a client fall back to their own project id, so nothing leaks.
export function templateScope(project) {
  if (!project) return "";
  if (project.clientId) return `client:${project.clientId}`;
  if (project.id) return `project:${project.id}`;
  return "";
}
